import { createInstance } from "i18next"
import { type MessageKey, en } from "@/i18n/en"
import type { useT } from "./i18n"
import type { Schemas } from "./api"
import type { DefinitionSet } from "./defs"
import { asArray, asString, isRecord } from "./utils"

export type Explanation = Schemas["PermissionExplainResponse"]
export type ConjunctOutcome = Schemas["PermissionConjunctOutcome"]
export type DeterminingPolicy = Schemas["PermissionDeterminingPolicy"]
export type Membership = Schemas["MembershipSummary"]
export type Delegation = Schemas["DelegationRecord"]

/** The production conjuncts in their short-circuit order, with what each one checks. */
export const CONJUNCTS = [
  { key: "scope", labelKey: "permissions.conjunct.scope", checksKey: "permissions.conjunct.scopeChecks" },
  { key: "capability", labelKey: "permissions.conjunct.capability", checksKey: "permissions.conjunct.capabilityChecks" },
  { key: "delegation", labelKey: "permissions.conjunct.delegation", checksKey: "permissions.conjunct.delegationChecks" },
  { key: "management", labelKey: "permissions.conjunct.management", checksKey: "permissions.conjunct.managementChecks" },
  { key: "access", labelKey: "permissions.conjunct.access", checksKey: "permissions.conjunct.accessChecks" },
] as const satisfies readonly { key: keyof Explanation["conjuncts"]; labelKey: MessageKey; checksKey: MessageKey }[]

export const OUTCOME_COPY: Record<ConjunctOutcome, { labelKey: MessageKey; tone: "success" | "danger" | "warning" | "neutral" }> = {
  allow: { labelKey: "permissions.outcome.allow", tone: "success" },
  deny: { labelKey: "permissions.outcome.deny", tone: "danger" },
  error: { labelKey: "permissions.outcome.error", tone: "warning" },
  notEvaluated: { labelKey: "permissions.outcome.notEvaluated", tone: "neutral" },
}

// Plain-module callers use English; components pass their current translator.
const english = createInstance()
void english.init({ lng: "en", resources: { en: { translation: en } }, keySeparator: false, interpolation: { escapeValue: false } })
const defaultT = english.getFixedT("en")

/** One declared AccessIR rule with the policy that declares it. */
export interface AccessRule {
  readonly artifactId: string
  readonly policyId: string
  readonly ruleId: string
  readonly actions: readonly string[]
  readonly resourceType: string
  readonly rule: Record<string, unknown>
}

/** A stable anchor for one rule: policy and rule ids together (rule ids are unique per policy only). */
export const ruleAnchor = (policyId: string, ruleId: string) => `${policyId}/${ruleId}`

/** Every admitted AccessIR rule. Obligation and approval policies are other PolicyRoot forms and are skipped. */
export function accessRules(set: DefinitionSet): AccessRule[] {
  return set.artifacts.flatMap((a) => {
    if (a.kind !== "policy" || !Array.isArray(a.payload.rules)) return []
    const policyId = asString(a.payload.policyId) ?? a.artifactId
    return asArray(a.payload.rules)
      .filter(isRecord)
      .map((rule) => ({ artifactId: a.artifactId, policyId, ruleId: asString(rule.ruleId) ?? "?", actions: asArray(rule.actions).map(String), resourceType: asString(rule.resourceType) ?? "?", rule }))
  })
}

/** Action ids a principal can be explained for: `norn.read` and every admitted action. */
export function explainableActions(set: DefinitionSet): string[] {
  const ids = set.artifacts.flatMap((a) => (a.kind === "action" ? [asString(a.payload.actionId)].filter((x): x is string => Boolean(x)) : []))
  return ["norn.read", ...[...new Set(ids)].sort()]
}

export type DelegationState = "active" | "revoked" | "expired"

/** A delegation's standing at `nowMs`: revocation wins over expiry, and neither is ever inferred as active. */
export function delegationState(d: Pick<Delegation, "revocation" | "expiresAtMs">, nowMs: number): DelegationState {
  if (d.revocation) return "revoked"
  return d.expiresAtMs <= nowMs ? "expired" : "active"
}

/** What one membership grants, in reading order: role, group, business unit. */
export function membershipGrant(m: Pick<Membership, "roleId" | "groupId" | "businessUnitId">, t: ReturnType<typeof useT> = defaultT): { kind: string; id: string }[] {
  return [
    ...(m.roleId ? [{ kind: t("permissions.membership.role"), id: m.roleId }] : []),
    ...(m.groupId ? [{ kind: t("permissions.membership.group"), id: m.groupId }] : []),
    ...(m.businessUnitId ? [{ kind: t("permissions.membership.businessUnit"), id: m.businessUnitId }] : []),
  ]
}

/** A validity window in the operator's terms; `null` bounds are open. Seconds since the Unix epoch. */
export function validity(from: number | null | undefined, until: number | null | undefined, t: ReturnType<typeof useT> = defaultT): string {
  const day = (s: number) => new Date(s * 1000).toISOString().slice(0, 10)
  if (from == null && until == null) return t("permissions.membership.always")
  if (from == null) return t("permissions.membership.until", { date: day(until as number) })
  if (until == null) return t("permissions.membership.from", { date: day(from) })
  return t("permissions.membership.validityRange", { from: day(from), until: day(until) })
}
